/**
 * SHER-1045 — what the nursing form's weight/height inputs turn into on "Add".
 *
 * Weight and height are independent measurements: a patient in a wheelchair can
 * be weighed without being measured. The IMC is derived, so it exists only when
 * both are present. A value that IS entered keeps the field's long-standing
 * validation (`Number(value)` must be truthy: not text, not zero).
 *
 * The entry always carries all three keys, with `null` for what is missing: the
 * API's `isValidBMI` checks `hasOwnProperty` on each, stores a null IMC master
 * record (the column is nullable) and skips a null slave, so a lone weight
 * persists as one master + one slave and reads back without the missing key.
 */
export type MeasurementInput = string | boolean;

export interface BmiEntry {
    bmi: number | null;
    bmi_height: number | null;
    bmi_weight: number | null;
}

export type BuildBmiEntryResult = { ok: true; entry: BmiEntry } | { ok: false };

function isBlank(value: MeasurementInput): boolean {
    return value === false || (typeof value === 'string' && value.trim() === '');
}

/** True when something was typed and it is not a usable number. Blank is valid. */
export function isInvalidMeasurement(value: MeasurementInput): boolean {
    return !isBlank(value) && !Number(value);
}

function toMeasurement(value: MeasurementInput): number | null {
    return isBlank(value) ? null : Number(value);
}

function computeBmi(weightKg: number | null, heightCm: number | null): number | null {
    if (weightKg == null || heightCm == null) return null;
    const heightMetres = heightCm / 100;
    return weightKg / (heightMetres * heightMetres);
}

export function buildBmiEntry(weight: MeasurementInput, height: MeasurementInput): BuildBmiEntryResult {
    if (isInvalidMeasurement(weight) || isInvalidMeasurement(height)) return { ok: false };
    const bmi_weight = toMeasurement(weight);
    const bmi_height = toMeasurement(height);
    if (bmi_weight == null && bmi_height == null) return { ok: false };
    return { ok: true, entry: { bmi: computeBmi(bmi_weight, bmi_height), bmi_height, bmi_weight } };
}

/**
 * A saved value read back from the API, completed before the form re-sends it.
 *
 * The API skips a null measurement on save, so a weight-only entry reads back
 * without `bmi_height` (and a height-only one without `bmi_weight`). The form
 * re-sends what it loaded on any edit of the submission, and `isValidBMI`
 * requires all three keys, so without this every later edit answers 400.
 */
export function completeBmiValue<T>(value: T): T {
    if (!Array.isArray(value)) return value;
    return value.map((entry) =>
        entry && typeof entry === 'object'
            ? { bmi: null, bmi_height: null, bmi_weight: null, ...entry }
            : entry,
    ) as unknown as T;
}

/**
 * The display parts of a saved BMI value, shared by the stacked summary (BmiSummary)
 * and the one-line table cell (formatBmiInline) so units and presence rules live once.
 */
export interface BmiParts {
    bmi: string | null;
    height: string | null;
    weight: string | null;
}

export function bmiParts(view: { bmi: number | null; height: number | null; weight: number | null }): BmiParts {
    return {
        bmi: view.bmi != null ? view.bmi.toFixed(2) : null,
        height: view.height != null ? `${view.height} cm` : null,
        weight: view.weight != null ? `${view.weight} kg` : null,
    };
}
